/**
 * Vote Buttons — pixel-faithful port of `design/Vote Buttons.dc.html`
 * (DC-RUNTIME §6.6).
 *
 * Contract notes:
 *  - The strip is the four calls in `CALLS` order; one tap picks one. `value`
 *    only *marks* the current call (`''` = no call yet) — the component holds
 *    no state and never toggles. A re-tap fires `onPick` with the same letter
 *    and the caller decides whether that clears (Detail's `vote()` does; What's
 *    New's `wnMark` does not, because the card advances).
 *  - Every letter, label and colour is read off `LVL`, so this file spells none
 *    of them; the copy has to match Tonight, Interests, Detail and the move
 *    sheet, and one table is the only way that stays true.
 *  - Single prototype call site: the What's New card, host
 *    `style={{width:'100%'}}` and `onPick` → `wnMark(l)` with **no `value`** —
 *    the card is marked and gone, so there is never a selection to render
 *    (INVENTORY §4.10, ported per porting/V.md V-06).
 *  - The pixel letter takes `translateY(0.13em)`: Press Start 2P sits high in
 *    its line box (`DESIGN-CLAUDE.md` "Optical centering"). Never rebalance it
 *    with uneven padding.
 */

import type { CSSProperties } from 'react';

import { CALLS, LVL } from '../lib/logic.ts';
import type { VoteInput, VoteValue } from '../lib/types.ts';

export interface VoteButtonsProps {
  /** the call to show as selected — DC default `''` (nothing selected) */
  value?: VoteInput;
  onPick?: (level: VoteValue) => void;
  /** host-wrapper style — positional properties only (the call sites pass `width:100%` / `flex:1`) */
  style?: CSSProperties;
}

/** Selected/unselected differ only in the three colour properties, applied below. */
const BTN: CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '7px',
  padding: '11px 8px',
  cursor: 'pointer',
};

const GLYPH: CSSProperties = {
  fontFamily: 'var(--gt-font-pixel)',
  fontSize: '13px',
  lineHeight: 1,
  transform: 'translateY(0.13em)',
};

const LABEL: CSSProperties = {
  fontFamily: 'var(--gt-font-mono)',
  fontSize: '8.5px',
  letterSpacing: '.06em',
  lineHeight: 1.4,
  textAlign: 'center',
};

export function VoteButtons({ value, onPick, style }: VoteButtonsProps) {
  const cur = value ?? '';
  const pick = onPick ?? (() => {});

  return (
    <div className="sc-host" data-sc-name="Vote Buttons" style={style}>
      <div style={{ display: 'flex', gap: '10px', width: '100%' }}>
        {CALLS.map((l) => {
          const on = cur === l;
          return (
            <div
              key={l}
              onClick={() => pick(l)}
              style={{
                ...BTN,
                border: '2px solid ' + (on ? LVL[l].c : 'var(--gt-border-2)'),
                color: on ? 'var(--gt-ink)' : LVL[l].c,
                background: on ? LVL[l].c : 'transparent',
              }}
            >
              <span style={GLYPH}>{LVL[l].g}</span>
              <span style={LABEL}>{LVL[l].t}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
